/**
 * quiz-progress.ts — 题库作答记录(单一真源)
 *
 * localStorage `gto-quiz-v1`: Record<id, { result, ts }>
 * quiz 页与 /stats 共用同一套 snapshot/subscribe/write,
 * 统计口径统一走 getQuizStats,不在页面里重复实现存储逻辑。
 *
 * 兼容:历史记录的 "revealed"(开放题只揭示未自评)保留在 union 里,
 * 计入 answered 但不计入 correct;新作答一律记 correct / wrong。
 */
import type { QuizQuestion } from "@/lib/quiz-types";

export const STORAGE_KEY = "gto-quiz-v1";
const RECORD_EVENT = "gto-quiz-record";

export type QuizResult = "correct" | "wrong" | "revealed";
export type QuizRecord = Record<string, { result: QuizResult; ts: number }>;

export const EMPTY_RECORD: QuizRecord = {};

let recordCache: QuizRecord = EMPTY_RECORD;
let recordRaw: string | null = null;

export function getRecordSnapshot(): QuizRecord {
  const raw = localStorage.getItem(STORAGE_KEY) || "{}";
  if (raw !== recordRaw) {
    recordRaw = raw;
    try {
      recordCache = JSON.parse(raw) as QuizRecord;
    } catch {
      recordCache = EMPTY_RECORD;
    }
  }
  return recordCache;
}

export function subscribeRecord(onChange: () => void): () => void {
  window.addEventListener("storage", onChange);
  window.addEventListener(RECORD_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(RECORD_EVENT, onChange);
  };
}

export function writeRecord(id: string, result: QuizResult) {
  try {
    const next = { ...getRecordSnapshot(), [id]: { result, ts: Date.now() } };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(RECORD_EVENT));
  } catch {
    // localStorage 不可用时静默丢弃
  }
}

export type QuizCategoryStat = {
  category: string;
  category_name: string;
  answered: number;
  correct: number;
};

export type QuizStats = {
  answered: number;
  correct: number;
  /** correct / answered;无作答时为 null */
  accuracy: number | null;
  byCategory: QuizCategoryStat[];
};

type QuestionMeta = Pick<QuizQuestion, "id" | "category" | "category_name">;

/**
 * 统计作答记录。传入 questions(题库元数据)时:
 * 只统计现行题库内的记录,并产出按类目拆分;不传则统计全部记录。
 */
export function getQuizStats(records: QuizRecord, questions?: QuestionMeta[]): QuizStats {
  const meta = questions ? new Map(questions.map((q) => [q.id, q] as const)) : null;
  const buckets = new Map<string, QuizCategoryStat>();
  let answered = 0;
  let correct = 0;
  for (const [id, r] of Object.entries(records)) {
    const q = meta?.get(id);
    if (meta && !q) continue;
    answered += 1;
    const isCorrect = r.result === "correct";
    if (isCorrect) correct += 1;
    if (q) {
      let bucket = buckets.get(q.category);
      if (!bucket) {
        bucket = {
          category: q.category,
          category_name: q.category_name,
          answered: 0,
          correct: 0,
        };
        buckets.set(q.category, bucket);
      }
      bucket.answered += 1;
      if (isCorrect) bucket.correct += 1;
    }
  }
  return {
    answered,
    correct,
    accuracy: answered > 0 ? correct / answered : null,
    byCategory: [...buckets.values()],
  };
}
